import { useCallback, useEffect, useState } from 'react';
import {
  ProcessingSpinnerModal,
  UpgradeFailureModal,
  UpgradeSuccessModal,
  useCreateCard,
  useGetPaymentMethod,
  useGetTokenExIframeSrc,
  useProcessCard,
  filterActivePaymentMethod,
} from '@ltvco/refresh-lib/payments';
import { Box, Grid, Stack, Text } from '@ltvco/refresh-lib/theme';
import { useSession, PaymentMethods } from '@ltvco/refresh-lib/v1';
import { useContext } from 'react';
import { AppConfig, AppConstants } from '@ltvco/refresh-lib/ctx';
import {
  PaymentSection,
  OrderSummary,
  FAQ,
  SearchTypes,
  TrustBadges,
  TrialOffer,
  BenefitsSection,
  LegalFooter,
  Header,
} from './components/index';
import { PageContainer, PromoBar } from './components/Header/components';
import {
  AcceptBand,
  AcceptTitle,
  AcceptLogos,
} from './components/MainContent/PaymentSection/components';
import { useScreenSize } from '@ltvco/refresh-lib/utils';
import {
  Bullet,
  BulletBackground,
  BulletNumber,
  StepHeader,
} from './components/MainContent/components';
import { useNavigate } from 'react-router-dom';
import { SUBSCRIPTION_PLAN_UNIQUE_NAME } from './components/constants';
import { useIframeManager } from './hooks/useIframeManager';
import {
  LegalFooterContent,
  LegalFooterLinks,
} from './components/Footer/components';

enum State {
  None,
  ProcessingPayment,
  Success,
  Error,
}

export const SubscribePage = () => {
  const { logError } = useContext(AppConfig);
  const [subscribeState, setSubscribeState] = useState(State.None);
  const [subscribeErrors, setSubscribeErrors] = useState<string[]>([]);
  const { useAccount, session } = useSession();
  const { refetch: refetchAccount } = useAccount(true);
  const { images } = useContext(AppConstants);
  const { isMobile } = useScreenSize();
  const navigate = useNavigate();

  const extendedImages = images as any;
  const account = session.account;

  useEffect(() => {
    const hasActiveSubscription =
      account?.account.subscription_info?.subscription_state === 'Active' &&
      !account?.account.subscription_info.subscription_features
        ?.teaser_reports_only;

    if (hasActiveSubscription) {
      navigate('/dashboard');
    }
  }, [account, navigate]);

  // Card payment form state
  const [email, setEmail] = useState(account?.account.user_info.email || '');
  const [firstName, setFirstName] = useState(
    account?.account.user_info.first_name || ''
  );
  const [lastName, setLastName] = useState(
    account?.account.user_info.last_name || ''
  );
  const [terms1, setTerms1] = useState(false);
  const [terms2, setTerms2] = useState(false);
  const [formErrors, setFormErrors] = useState({
    email: '',
    cardNumber: '',
    terms1: '',
    terms2: '',
  });

  // TokenEx state is now managed by TokenExSubscribe component
  const { data: iframeSrc } = useGetTokenExIframeSrc();

  // Use the new iframe manager hook
  const { resetIframeState } = useIframeManager({
    iframeSrc,
    paymentMethod: 'card',
    subscribeErrors,
    onError: (message: string) => {
      logError('Error on iframe manager', new Error(message));
      setSubscribeErrors([message]);
      setSubscribeState(State.Error);
    },
  });
  const createCardQuery = useCreateCard();
  const paymentMethodQuery = useGetPaymentMethod();
  const processCardQuery = useProcessCard();
  const [activePayment, setActivePayment] = useState<PaymentMethods>();
  const [paymentMethodAdded, setPaymentMethodAdded] = useState(false);

  // Payment processing hooks

  useEffect(() => {
    if (!paymentMethodQuery.isLoading && !paymentMethodQuery.isError) {
      const newActivePayment = filterActivePaymentMethod(
        paymentMethodQuery?.data
      );
      setActivePayment(newActivePayment);

      // If a payment method was just added and we have an active payment, proceed with subscription
      if (paymentMethodAdded && newActivePayment) {
        setPaymentMethodAdded(false);
        processSubscription();
      }
    }
  }, [paymentMethodQuery, paymentMethodAdded]);

  const processSubscription = useCallback(() => {
    if (!activePayment) return;

    setSubscribeState(State.ProcessingPayment);

    const plan = { unique_key: SUBSCRIPTION_PLAN_UNIQUE_NAME };
    const queryOptions: any = {
      onSuccess: () => {
        refetchAccount();
        setSubscribeState(State.Success);
      },
      onError(error: Error) {
        let cause = error.cause as string[];
        const stringifiedErrors = cause?.join(', ');

        if (stringifiedErrors === 'Unknown Checkout Failure Reason') {
          cause = [
            'Your payment failed, please try again or choose a different payment method.',
          ];
        }

        setSubscribeErrors(cause);
        setSubscribeState(State.Error);
        logError('Error processing subscription', error as Error);
      },
    };

    const mutationParams = {
      plan,
      cardId: activePayment.id,
      source_of_payment: 'subscription_signup',
    };

    processCardQuery.mutate(mutationParams, queryOptions);
  }, [activePayment, processCardQuery, refetchAccount, logError]);

  const handlePaymentMethodAdded = useCallback(() => {
    setPaymentMethodAdded(true);
    // Refresh payment methods to get the newly added one
    paymentMethodQuery.refetch();
  }, [paymentMethodQuery]);

  // Stable handlers to avoid inline functions in props
  const handleProcessingStart = useCallback(() => {
    setSubscribeState(State.ProcessingPayment);
  }, []);

  const handleSuccess = useCallback(() => {
    setSubscribeState(State.Success);
    refetchAccount();
  }, [refetchAccount]);

  const handleError = useCallback((errors?: string[]) => {
    setSubscribeErrors(errors ?? ['An unknown error occurred.']);
    setSubscribeState(State.Error);
  }, []);

  const handleUpgradeSuccessAccept = useCallback(() => {
    setSubscribeState(State.None);
    navigate('/dashboard');
  }, [navigate]);

  const handleUpgradeFailureTryAgain = useCallback(() => {
    setSubscribeState(State.None);
  }, []);

  const handleUpgradeFailureChangeCard = useCallback(() => {
    setSubscribeState(State.None);
  }, []);

  const handleUpgradeFailureClose = useCallback(() => {
    setSubscribeState(State.None);
  }, []);

  useEffect(() => {
    document.title = 'Subscribe - Bumper';
  }, []);

  useEffect(() => {
    // Since we only support card, this can be simplified.
    // However, leaving the hook in case other methods are re-added.
    // A non-card method would require a reset.
    resetIframeState();
  }, [resetIframeState]);

  return (
    <Box sx={{ backgroundColor: 'common.white', minHeight: '100vh' }}>
      <PromoBar>
        {isMobile ? 'Special Offer!' : 'Take advantage of our special offer!'}
      </PromoBar>
      <PageContainer>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <Header />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Text
              variant="h1"
              textAlign="center"
              fontWeight={400}
              fontStyle="italic"
              sx={{ mt: 2 }}
              fontSize={{ xs: 20, md: 40 }}
            >
              Run reports for just $1*
            </Text>
          </Grid>

          <Grid size={{ xs: 12, md: 7 }}>
            <Stack spacing={2}>
              <Box>
                <StepHeader>
                  <Bullet>
                    <BulletBackground
                      src={extendedImages.subscribePage.shieldIcon}
                      alt=""
                    />
                    <BulletNumber>1</BulletNumber>
                  </Bullet>
                  <Text
                    variant="h3"
                    fontWeight={800}
                    mb={0}
                    color="textPrimary"
                  >
                    Special $1 Trial Offer
                  </Text>
                </StepHeader>
                <TrialOffer isSelected={true} onSelect={() => {}} />
                <Box sx={{ mt: 1.5 }}>
                  <AcceptBand>
                    <AcceptTitle>WE ACCEPT</AcceptTitle>
                    <AcceptLogos>
                      <img
                        src={extendedImages.subscribePage.creditCardsLogo}
                        alt="Cards"
                        height={28}
                      />
                    </AcceptLogos>
                  </AcceptBand>
                </Box>

                {/* Mobile: Show sidebar components after AcceptBand */}
                {isMobile && (
                  <Box sx={{ mt: 2 }}>
                    <Stack spacing={2}>
                      <FAQ />
                    </Stack>
                  </Box>
                )}
              </Box>

              <Box>
                <StepHeader>
                  <Bullet>
                    <BulletBackground
                      src={extendedImages.subscribePage.shieldIcon}
                      alt=""
                    />
                    <BulletNumber>2</BulletNumber>
                  </Bullet>
                  <Text
                    variant="h3"
                    fontWeight={800}
                    mb={0}
                    color="textPrimary"
                  >
                    Your membership benefits include
                  </Text>
                </StepHeader>
                <BenefitsSection />
              </Box>

              <Box>
                <StepHeader>
                  <Bullet>
                    <BulletBackground
                      src={extendedImages.subscribePage.shieldIcon}
                      alt=""
                    />
                    <BulletNumber>3</BulletNumber>
                  </Bullet>
                  <Text
                    variant="h3"
                    fontWeight={800}
                    mb={0}
                    color="textPrimary"
                  >
                    Final step - just enter your details
                  </Text>
                </StepHeader>
                {isMobile && <OrderSummary />}
                <PaymentSection
                  paymentMethod="card"
                  setPaymentMethod={() => {}}
                  terms1={terms1}
                  setTerms1={setTerms1}
                  terms2={terms2}
                  setTerms2={setTerms2}
                  email={email}
                  firstName={firstName}
                  lastName={lastName}
                  formErrors={formErrors}
                  createCardQuery={createCardQuery}
                  extendedImages={extendedImages}
                  onProcessingStart={handleProcessingStart}
                  onSuccess={handleSuccess}
                  onError={handleError}
                  onPaymentMethodAdded={handlePaymentMethodAdded}
                />
              </Box>
            </Stack>
          </Grid>

          <Grid size={{ xs: 12, md: 5 }} mt={{ md: 5.5, xs: 0 }}>
            <Stack spacing={2}>
              {!isMobile && <OrderSummary />}
              {!isMobile && <FAQ />}
              {!isMobile && <SearchTypes />}
              <TrustBadges />
            </Stack>
          </Grid>
        </Grid>
        <LegalFooter />
      </PageContainer>
      <Stack width="100%" alignItems="center" justifyContent="center">
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems="center"
          spacing={2}
          justifyContent="space-between"
          width="80%"
          mt={2}
          textAlign="left"
        >
          <LegalFooterContent variant="body2">
            © {new Date().getFullYear()} Bumper.com. All rights reserved.
            Bumper®, and the BUMPER logo are trademarks of Bumper LLC.{' '}
          </LegalFooterContent>
          <Box>
            <LegalFooterLinks
              href="https://www.bumper.com/terms/"
              variant="body2"
              target="_blank"
              rel="noopener noreferrer"
            >
              Terms & Conditions
            </LegalFooterLinks>{' '}
            |{' '}
            <LegalFooterLinks
              href="https://www.bumper.com/privacy-policy/"
              variant="body2"
              target="_blank"
              rel="noopener noreferrer"
            >
              Privacy Policy
            </LegalFooterLinks>{' '}
            |{' '}
            <LegalFooterLinks
              href="https://www.bumper.com/nmvtis-disclaimer/"
              variant="body2"
              target="_blank"
              rel="noopener noreferrer"
            >
              NMVTIS Disclaimer
            </LegalFooterLinks>
          </Box>
        </Stack>
      </Stack>
      <ProcessingSpinnerModal
        open={subscribeState === State.ProcessingPayment}
      />
      <UpgradeSuccessModal
        open={subscribeState === State.Success}
        onAccept={handleUpgradeSuccessAccept}
      />
      <UpgradeFailureModal
        open={subscribeState === State.Error}
        errors={subscribeErrors}
        onTryAgain={handleUpgradeFailureTryAgain}
        onChangeCard={handleUpgradeFailureChangeCard}
        onClose={handleUpgradeFailureClose}
      />
    </Box>
  );
};
